"use client";

import { useState } from "react";
import swal from "sweetalert";
import { useResetUserPassword } from "@/modules/user/infraestructure/services/auth";

export default function ResetPasswordModal({
	uid,
	fullName,
	email,
	onClose,
}: {
	uid: string;
	fullName: string;
	email: string | null | undefined;
	onClose: () => void;
}) {
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [show, setShow] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const resetPassword = useResetUserPassword();

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		if (password.length < 6) {
			setError("La contraseña debe tener al menos 6 caracteres.");
			return;
		}
		if (password !== confirm) {
			setError("Las contraseñas no coinciden.");
			return;
		}

		resetPassword.mutate(
			{ uid, password },
			{
				onSuccess: () => {
					onClose();
					swal(
						"Contraseña actualizada",
						`Se actualizó la contraseña de ${fullName}. Comunicásela al usuario.`,
						"success",
					);
				},
				onError: (err: unknown) => {
					setError(err instanceof Error ? err.message : "Error desconocido");
				},
			},
		);
	};

	return (
		<div
			className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
			onClick={onClose}
		>
			<div
				className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
				onClick={(e) => e.stopPropagation()}
			>
				<h2 className="text-lg font-semibold text-grey-900 mb-1">
					Restablecer contraseña
				</h2>
				<p className="text-sm text-grey-500 mb-1">{fullName}</p>
				{email ? (
					<p className="text-xs text-grey-400 mb-5">{email}</p>
				) : (
					<p className="text-xs text-yellow-700 mb-5">
						Este usuario no tiene email cargado — no se va a poder actualizar la contraseña.
					</p>
				)}
				<form onSubmit={handleSubmit} className="flex flex-col gap-3">
					<label className="text-sm text-grey-700">
						Nueva contraseña
						<input
							type={show ? "text" : "password"}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							autoComplete="new-password"
							className="w-full mt-1 px-3 py-2 border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
							required
							minLength={6}
							placeholder="Mínimo 6 caracteres"
							autoFocus
						/>
					</label>
					<label className="text-sm text-grey-700">
						Confirmar contraseña
						<input
							type={show ? "text" : "password"}
							value={confirm}
							onChange={(e) => setConfirm(e.target.value)}
							autoComplete="new-password"
							className="w-full mt-1 px-3 py-2 border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300"
							required
							minLength={6}
						/>
					</label>
					<label className="flex items-center gap-2 text-xs text-grey-500">
						<input
							type="checkbox"
							checked={show}
							onChange={(e) => setShow(e.target.checked)}
						/>
						Mostrar contraseña
					</label>
					{error && (
						<p className="text-sm text-red-600 mt-1" role="alert">
							{error}
						</p>
					)}
					<div className="flex gap-2 justify-end mt-3">
						<button
							type="button"
							onClick={onClose}
							disabled={resetPassword.isPending}
							className="px-4 py-2 text-sm text-grey-700 hover:bg-grey-100 rounded-md disabled:opacity-50"
						>
							Cancelar
						</button>
						<button
							type="submit"
							disabled={resetPassword.isPending || !email}
							className="px-4 py-2 text-sm bg-primary-700 text-white hover:bg-primary-800 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{resetPassword.isPending ? "Guardando..." : "Cambiar contraseña"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
